import { inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';

import {
  ListEnumModel,
  ListRequestModel,
  ListResponseEnumModel,
  ListResponseModel
} from '@app/core/models/crud/list.model';
import { CreateResponseModel } from '../models/crud/create.model';

export abstract class BaseHttpService {
  private _http = inject(HttpClient);

  protected get baseApiUrl() {
    return environment.baseApiUrl;
  }

  protected get httpClient() {
    return this._http;
  }

  protected buildOptions() {
    return { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
  }

  protected getPage<T>(url: string, request: ListRequestModel): Observable<ListResponseModel<T>> {
    const params = request.filter ? { ...request.pageInfo, ...request.filter } : request.pageInfo;
    return this.httpClient.get<ListResponseModel<T>>(`${this.baseApiUrl}${url}`, {
      params: this.buildQueryParams(params)
    });
  }

  protected getAll<T>(url: string): Observable<T[]> {
    return this.httpClient.get<T[]>(`${this.baseApiUrl}${url}`);
  }

  protected getAllEnumItems(url: string): Observable<ListEnumModel[]> {
    return this.httpClient
      .get<ListResponseEnumModel>(`${this.baseApiUrl}${url}`)
      .pipe(map((response) => response.data));
  }

  protected getDetails<T>(url: string, uid: string): Observable<T> {
    return this.httpClient.get<T>(`${this.baseApiUrl}${url}/${uid}`);
  }

  protected create<T>(url: string, data: T): Observable<CreateResponseModel> {
    const body = JSON.stringify(data);
    const options = this.buildOptions();

    return this.httpClient.post<CreateResponseModel>(`${this.baseApiUrl}${url}`, body, options);
  }

  protected update<T>(url: string, data: T): Observable<void> {
    const body = JSON.stringify(data);
    const options = this.buildOptions();

    return this.httpClient.put<void>(`${this.baseApiUrl}${url}`, body, options);
  }

  protected delete(url: string, uid: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseApiUrl}${url}/${uid}`);
  }

  protected save<T>(url: string, data: T): Observable<void> {
    const body = JSON.stringify(data as T);
    const options = this.buildOptions();

    return this.httpClient.post<void>(`${this.baseApiUrl}${url}`, body, options);
  }

  protected buildQueryParams(filter: unknown | undefined): HttpParams {
    let params = new HttpParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }
    return params;
  }
}
